import React from "react";
import { useReveal, Photo, Words, PillLink, TextLink, SectionHead, GetInvolved } from "../Components/shared";
import { ORG, VISION, MISSION, ABOUT_BODY, PILLARS, SOLUTIONS } from "../data/site";

// Each pillar is told through a photograph rather than an icon.
const PILLAR_PHOTOS = { learn: "computerLab", community: "playingAmpe", code: "mentorLab" };

function Hero() {
  return (
    <section className="hero" aria-label="Introduction">
      <Photo name="heroLaptop" priority sizes="100vw" className="hero-photo" />
      <div className="hero-scrim" aria-hidden="true" />
      <div className="wrap hero-inner">
        <p className="kicker on-dark" data-reveal>{ORG.expansion}</p>
        <h1 className="display" data-reveal="words">
          <Words lines={["Transforming minds,", "shaping the future."]} />
        </h1>
        <p className="lead hero-lead" data-reveal style={{ "--d": 4 }}>
          Hands-on IT training and mentorship for young people in underserved communities,
          so that IT literacy becomes a bridge, not a barrier.
        </p>
        <div className="btn-row" data-reveal style={{ "--d": 5 }}>
          <PillLink to="/joinUs">Join EMANAC</PillLink>
          <PillLink to="/services" variant="light">Our programmes</PillLink>
        </div>
      </div>
    </section>
  );
}

function Home() {
  const ref = useReveal();

  return (
    <main id="main" ref={ref}>
      <Hero />

      <section className="section" id="about">
        <div className="wrap story">
          <div className="story-copy">
            <p className="kicker" data-reveal>Who we are</p>
            <p className="statement" data-reveal>
              We bridge the gap in IT literacy, so young people become <mark>active contributors</mark> to the global tech space.
            </p>
            <p className="body-lg" data-reveal>{ABOUT_BODY[1]}</p>
            <div data-reveal><TextLink to="/about">Our story</TextLink></div>
          </div>
          <div className="story-photos">
            <Photo name="twoFriendsTall" className="story-photo-a" sizes="(max-width: 860px) 60vw, 26vw" />
            <Photo name="smilingClassroom" className="story-photo-b" sizes="(max-width: 860px) 70vw, 30vw" />
          </div>
        </div>
      </section>

      <section className="section section-mist">
        <div className="wrap">
          <SectionHead kicker="What we do" title="Three pillars, one purpose.">
            Everything we do moves young people from curious to capable, and from capable to contributing.
          </SectionHead>
          <div className="pillars">
            {PILLARS.map((p, i) => (
              <article key={p.title} className="pillar" data-reveal style={{ "--d": i }}>
                <Photo name={PILLAR_PHOTOS[p.icon]} reveal={false} sizes="(max-width: 860px) 100vw, 33vw" />
                <div className="pillar-body">
                  <span className="pillar-num">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="h3">{p.title}</h3>
                  <p className="body">{p.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section dark">
        <div className="wrap purpose">
          <Photo name="girlsLaptop" className="purpose-photo" sizes="(max-width: 860px) 100vw, 50vw" />
          <div className="purpose-copy">
            <div className="purpose-item" data-reveal>
              <p className="kicker on-dark">Our vision</p>
              <p className="purpose-text">{VISION}</p>
            </div>
            <div className="purpose-item" data-reveal style={{ "--d": 1 }}>
              <p className="kicker on-dark">Our mission</p>
              <p className="purpose-text">{MISSION}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="solutions">
        <div className="wrap">
          <div className="solutions-head">
            <SectionHead kicker="Software solutions" title="The people who teach also build.">
              Bring us your idea or your problem, and our engineers will design, build and support
              the solution with you.
            </SectionHead>
            <div className="btn-row" data-reveal>
              <PillLink to="/contact" variant="ink">Start a project</PillLink>
              <TextLink to="/services#solutions">All services</TextLink>
            </div>
          </div>
          <ol className="numbered two">
            {SOLUTIONS.map((s, i) => (
              <li key={s.title} data-reveal style={{ "--d": i }}>
                <span className="numbered-n">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="h3">{s.title}</h3>
                  <p className="body">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <GetInvolved />
    </main>
  );
}

export default Home;
